import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { requestNewProfile } from "app/services/profileServices/Profile";
import * as Yup from "yup";
import { Formik } from "formik";
import { LoadingButton } from "@mui/lab";
import SnackbarProvider from "app/components/SnackbarProvider";
import { ArrowRightAltOutlined } from "@mui/icons-material";
import { notify } from "app/utils/notifyUtils";

const style = {
  position: "absolute" as "absolute",
  top: "20%",
  left: "50%",
  transform: "translate(-50%, -20%)",
  width: "30%",
  bgcolor: "background.paper",
  border: "1px solid #CCC",
  borderRadius: "5px",
  boxShadow: 24,
  p: 4,
};

const initialValues = {
  email: "",
  function: "",
  orcid: "",
  institution: "",
};

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Endereço de email inválido")
    .required("Email é obrigatório"),
});

const validationSchemaForManagers = Yup.object().shape({
  //orcid: Yup.string().required("ORCID é obrigatório"),
  function: Yup.string().required("Função é obrigatório"),
  institution: Yup.string().required("Instituição é obrigatório"),
});

interface FormValues {
  email: string;
  orcid: string;
  function: string;
  institution: string;
}

const FormApprovationRequest = ({
  selectedProfile,
  reloadPrevComponent,
  handleClose,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const snackbarProvider = SnackbarProvider();
  const [checked, setChecked] = useState(
    selectedProfile.profile_manager_id === 2 ? false : true
  );

  const handleAcceptPayment = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  const handleFormSubmit = async (values: FormValues) => {
    setLoading(true);
    try {
      await requestNewProfile(
        selectedProfile.id,
        values.email,
        values.orcid,
        values.function,
        values.institution
      );
      setLoading(false);
      reloadPrevComponent();
      handleClose();
      notify("success", "Um email foi encaminhado ao responsável selecionado");
    } catch (e: any) {
      setLoading(false);
      notify("error", e.response.data.response);
    }
  };

  const ApprovedImportantText = ({ selectedProfile }) => {
    const approvedProfileMessage = [6, 8, 9, 10].includes(selectedProfile.id)
      ? `Ao solicitar a ativação de um perfil ${selectedProfile.name}, 
        você deverá solicitar a aprovação de um Pesquisador Responsável, para isso, informe o e-mail do responsável no campo abaixo que iremos solicitar a sua aprovação.
        Mas não se preocupe, assim que seu perfil for aprovado, lhe avisaremos por e-mail.`
      : `Ao solicitar a ativação de um perfil ${selectedProfile.name}, você deverá aguardar a aprovação de um Administrador do LCE, 
        mas não se preocupe, assim que seu perfil for aprovado, lhe avisaremos por e-mail.`;

    return <>{approvedProfileMessage}</>;
  };

  return (
    <div>
      <Box sx={style}>
        <Grid
          container
          spacing={3}
          alignItems="center"
          sx={{ marginBottom: "2rem" }}
        >
          <Grid item xs={2}>
            <img
              src={require(`../../../assets/icons/${selectedProfile.icon}.png`)}
              alt={selectedProfile.icon}
              width={60}
              style={{ display: "block", margin: "0 auto" }}
            />
          </Grid>
          <Grid item xs={10}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {selectedProfile.name}
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ marginBottom: "2rem" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: "#1976d2", marginBottom: "0.5rem" }}
          >
            Importante:
          </Typography>
          <Typography variant="body1" sx={{ color: "#555" }}>
            <ApprovedImportantText selectedProfile={selectedProfile} />
          </Typography>
        </Box>

        <Formik
          onSubmit={handleFormSubmit}
          initialValues={initialValues}
          validationSchema={
            selectedProfile.profile_manager_id === 2
              ? validationSchemaForManagers
              : validationSchema
          }
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
          }) => (
            <form onSubmit={handleSubmit}>
              <Box
                sx={{
                  display:
                    selectedProfile.profile_manager_id === 2 ? "none" : "block",
                  marginBottom: "1.5rem",
                }}
              >
                <TextField
                  fullWidth
                  name="email"
                  label="E-mail do Responsável"
                  size="medium"
                  onBlur={handleBlur}
                  value={values.email}
                  onChange={handleChange}
                  helperText={touched.email && errors.email}
                  error={Boolean(errors.email)}
                  sx={{ backgroundColor: "#f8f9fa" }}
                />
              </Box>

              <Box
                sx={{
                  display:
                    selectedProfile.profile_manager_id === 2 ? "block" : "none",
                  "& .MuiTextField-root": { marginBottom: "1rem" },
                }}
              >
                <TextField
                  fullWidth
                  name="orcid"
                  label="ORCID"
                  size="medium"
                  onBlur={handleBlur}
                  value={values.orcid}
                  onChange={handleChange}
                  helperText={touched.orcid && errors.orcid}
                  error={Boolean(errors.orcid)}
                  sx={{ backgroundColor: "#f8f9fa" }}
                />
                <TextField
                  fullWidth
                  name="function"
                  label="Função"
                  size="medium"
                  onBlur={handleBlur}
                  value={values.function}
                  onChange={handleChange}
                  helperText={touched.function && errors.function}
                  error={Boolean(errors.function)}
                  sx={{ backgroundColor: "#f8f9fa" }}
                />
                <TextField
                  fullWidth
                  name="institution"
                  label="Instituição"
                  size="medium"
                  onBlur={handleBlur}
                  value={values.institution}
                  onChange={handleChange}
                  helperText={touched.institution && errors.institution}
                  error={Boolean(errors.institution)}
                  sx={{ backgroundColor: "#f8f9fa" }}
                />
                <FormControlLabel
                  sx={{ marginTop: "1rem" }}
                  label={
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      Termos de uso
                    </Typography>
                  }
                  control={
                    <Checkbox
                      checked={checked}
                      onChange={handleAcceptPayment}
                      sx={{ color: "#1976d2" }}
                    />
                  }
                />
              </Box>

              <LoadingButton
                fullWidth
                type="submit"
                color="primary"
                disabled={!checked}
                loading={loading}
                variant="contained"
                sx={{
                  mt: 3,
                  py: 1.5,
                  textTransform: "none",
                  borderRadius: "8px",
                  boxShadow: "none",
                  "&:hover": {
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  },
                }}
                endIcon={<ArrowRightAltOutlined />}
              >
                Solicitar Perfil
              </LoadingButton>
            </form>
          )}
        </Formik>
      </Box>
    </div>
  );
};

export default FormApprovationRequest;
